import type {
  EntityTiming,
  OrderStatusTimeOrder,
  StatusTimeSegment,
} from "../api/order-status-time.api";

/** Persisted fulfillment statuses, earliest first. Matches the duration clock. */
const STATUS_TIME_ORDER = [
  "SCRIPT",
  "SENT_TO_PRODUCTION",
  "SUBMITTED_FOR_FEEDBACK",
  "SENT_FOR_REVISION",
  "SUBMITTED_FOR_FINAL_APPROVAL",
  "ORDER_DELIVERED",
  "COMPLETED_PRE_ADGE",
  "CANCELLED",
] as const;

export interface ClientBreakdown {
  key: string;
  clientName: string;
  orders: OrderStatusTimeOrder[];
  timing: EntityTiming;
}

export interface PartnerGroup {
  key: string;
  partnerName: string;
  clients: ClientBreakdown[];
  timing: EntityTiming;
}

const EMPTY_TIMING: EntityTiming = {
  segments: [],
  totalMs: 0,
  breakdownAvailable: false,
  isOpen: false,
};

/** Mean of the order clocks. An order that never entered a step counts as zero for that step. */
function averageTimings(timings: EntityTiming[]): EntityTiming {
  const count = timings.length;
  if (count === 0) return EMPTY_TIMING;

  const totalMs = Math.round(timings.reduce((sum, timing) => sum + timing.totalMs, 0) / count);
  const isOpen = timings.some((timing) => timing.isOpen);
  const explained = timings.filter((timing) => timing.breakdownAvailable);
  if (explained.length === 0) {
    return { segments: [], totalMs, breakdownAvailable: false, isOpen };
  }

  const byStatus = new Map<string, { label: string; durationMs: number; isOpen: boolean }>();
  for (const timing of explained) {
    for (const segment of timing.segments) {
      const current = byStatus.get(segment.status);
      if (current) {
        current.durationMs += segment.durationMs;
        current.isOpen = current.isOpen || segment.isOpen;
      } else {
        byStatus.set(segment.status, {
          label: segment.label,
          durationMs: segment.durationMs,
          isOpen: segment.isOpen,
        });
      }
    }
  }

  const known = new Set<string>(STATUS_TIME_ORDER);
  const segments: StatusTimeSegment[] = [];
  const pushSegment = (status: string, row: { label: string; durationMs: number; isOpen: boolean }) => {
    const durationMs = Math.round(row.durationMs / count);
    if (durationMs === 0) return;
    segments.push({
      status,
      label: row.label,
      durationMs,
      isOpen: isOpen && row.isOpen,
    });
  };
  for (const status of STATUS_TIME_ORDER) {
    const row = byStatus.get(status);
    if (row) pushSegment(status, row);
  }
  for (const [status, row] of byStatus) {
    if (!known.has(status)) pushSegment(status, row);
  }

  return { segments, totalMs, breakdownAvailable: true, isOpen };
}

/** Average of each order's own clock, not the sum of its scripts. */
export function averageOrderTiming(orders: OrderStatusTimeOrder[]): EntityTiming {
  return averageTimings(orders.map((order) => order.timing));
}

function partnerKey(partnerName: string): string {
  return JSON.stringify([partnerName]);
}

function clientKey(clientName: string): string {
  return JSON.stringify([clientName]);
}

/**
 * One row per partner, in the order their newest order arrived.
 * Each partner holds one row per client, with that pair's orders underneath.
 */
export function groupOrdersByPartner(orders: OrderStatusTimeOrder[]): PartnerGroup[] {
  const partners = new Map<string, { partnerName: string; clients: Map<string, ClientBreakdown> }>();

  for (const order of orders) {
    const key = partnerKey(order.partnerName);
    let partner = partners.get(key);
    if (!partner) {
      partner = { partnerName: order.partnerName, clients: new Map() };
      partners.set(key, partner);
    }

    const childKey = clientKey(order.clientName);
    const existing = partner.clients.get(childKey);
    if (existing) {
      existing.orders.push(order);
    } else {
      partner.clients.set(childKey, {
        key: childKey,
        clientName: order.clientName,
        orders: [order],
        timing: EMPTY_TIMING,
      });
    }
  }

  return [...partners.entries()].map(([key, partner]) => {
    const clients = [...partner.clients.values()];
    for (const client of clients) {
      client.timing = averageOrderTiming(client.orders);
    }
    return {
      key,
      partnerName: partner.partnerName,
      clients,
      timing: averageOrderTiming(clients.flatMap((client) => client.orders)),
    };
  });
}
